const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

type ApiTransportState =
  | { mode: "native" }
  | {
      mode: "bizcentral";
      getToken: () => string | null;
      invalidate: (reason: "backend_unauthorized" | "backend_forbidden") => void;
    };

let transportState: ApiTransportState = { mode: "native" };

export class MissingBizcentralTokenError extends Error {
  constructor() {
    super("A valid Bizcentral session is required.");
    this.name = "MissingBizcentralTokenError";
  }
}

export function configureBizcentralApiTransport(
  getToken: () => string | null,
  invalidate: (reason: "backend_unauthorized" | "backend_forbidden") => void,
): void {
  transportState = { mode: "bizcentral", getToken, invalidate };
}

export function configureNativeApiTransport(): void {
  transportState = { mode: "native" };
}

function requestTarget(path: string): string {
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path}`;
}

function browserOrigin(): string {
  return typeof window === "undefined" ? "http://localhost" : window.location.origin;
}

function isTicketingBackendTarget(target: string): boolean {
  try {
    const origin = browserOrigin();
    const apiBase = new URL(API_BASE_URL || "/", origin);
    const targetUrl = new URL(target, origin);
    const basePath = apiBase.pathname.endsWith("/") ? apiBase.pathname : `${apiBase.pathname}/`;
    return (
      targetUrl.origin === apiBase.origin &&
      (targetUrl.pathname === apiBase.pathname || targetUrl.pathname.startsWith(basePath))
    );
  } catch {
    return false;
  }
}

export async function ticketingFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const target = requestTarget(path);
  if (transportState.mode === "native") {
    return fetch(target, { ...init, credentials: "include" });
  }

  if (!isTicketingBackendTarget(target)) {
    return fetch(target, { ...init, credentials: "omit" });
  }

  const token = transportState.getToken();
  if (!token) {
    throw new MissingBizcentralTokenError();
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(target, { ...init, credentials: "omit", headers });
  if (response.status === 401 || response.status === 403) {
    transportState.invalidate(response.status === 401 ? "backend_unauthorized" : "backend_forbidden");
  }
  return response;
}
